import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { formatDuration, formatTime } from '../../lib/format.js'
import { useIsTablet } from '../../lib/media.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Modal } from '../Modal.js'
import { Sheet } from '../Sheet.js'
import { nextCheckIn } from '../VisitActions.js'
import { Badge, Spinner, VISIT_STATUS_TONE, humanise } from '../ui.js'

/**
 * The field app's parts, drawn to the field-app iOS mockup.
 *
 * The office screens are dense on purpose; these are the opposite, because
 * they are read one-handed in a van. A 34px title, grouped white cards on the
 * ink-100 page, 13px uppercase section labels and rows that are at least 52px
 * tall. Everything here takes its colour from the same tokens as the office
 * screens, so the two stay one product.
 */

export const cardClass = 'overflow-hidden rounded-[14px] bg-white ring-1 ring-ink-200 ring-inset'

/** A row inside a `Group`. Tall enough for a thumb, hairline between. */
export const rowClass =
  'flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-ink-50'

export function FieldScreen({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3.5 px-4 pt-1.5 pb-6', className)}>{children}</div>
  )
}

export function ScreenHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow?: ReactNode
  title: string
  action?: ReactNode
}) {
  return (
    <header className="flex items-end gap-3 pt-1.5 pb-0.5">
      <div className="min-w-0 flex-1">
        {eyebrow ? (
          <p className="text-[13px] font-semibold tracking-[0.02em] text-ink-500 uppercase">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="mt-0.5 font-display text-[30px] leading-[1.1] font-extrabold text-brand-800">
          {title}
        </h1>
      </div>
      {action ? <div className="shrink-0 pb-1">{action}</div> : null}
    </header>
  )
}

export function GroupLabel({
  children,
  aside,
  tone,
}: {
  children: ReactNode
  aside?: ReactNode
  tone?: 'amber'
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 px-1 pb-1.5">
      <span
        className={cn(
          'text-[13px] font-semibold tracking-[0.03em] uppercase',
          tone === 'amber' ? 'text-amber-800' : 'text-ink-500',
        )}
      >
        {children}
      </span>
      {aside ? <span className="text-[13px] text-ink-500">{aside}</span> : null}
    </div>
  )
}

/** A labelled card of rows. Hairlines between rows come from `divide-y`. */
export function Group({
  label,
  aside,
  tone,
  children,
  className,
}: {
  label?: ReactNode
  aside?: ReactNode
  tone?: 'amber'
  children: ReactNode
  className?: string
}) {
  return (
    <section>
      {label ? (
        <GroupLabel aside={aside} tone={tone}>
          {label}
        </GroupLabel>
      ) : null}
      <div className={cn(cardClass, 'divide-y divide-ink-200', className)}>{children}</div>
    </section>
  )
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
  label: string
  className?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('flex gap-0.5 rounded-[10px] bg-ink-200/70 p-0.5', className)}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'h-8 flex-1 rounded-lg px-3 text-[13.5px] font-semibold transition-colors',
              active ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-600',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function FieldSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
}) {
  return (
    <label className="flex h-11 items-center gap-2 rounded-xl bg-white px-3 ring-1 ring-ink-200 ring-inset">
      <Icon name="search" className="size-[18px] shrink-0 text-ink-500" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 bg-transparent text-base text-ink-900 outline-none placeholder:text-ink-400"
      />
    </label>
  )
}

export function VisitBadge({ status }: { status: string }) {
  return (
    <Badge tone={VISIT_STATUS_TONE[status] ?? 'neutral'} className="rounded-md px-2 py-0.5 text-xs">
      {humanise(status)}
    </Badge>
  )
}

/**
 * Midnight to midnight. Today and Team ask `schedule.board` with exactly these,
 * so the tab badge and the screen share one cached query.
 */
export function todayWindow(): { from: Date; to: Date } {
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  const to = new Date(from)
  to.setDate(to.getDate() + 1)
  return { from, to }
}

/** "9:00 am – 10:30 am". */
export function timeRange(startAt: Date | string, endAt: Date | string): string {
  return `${formatTime(startAt)} – ${formatTime(endAt)}`
}

export function lengthOf(startAt: Date | string, endAt: Date | string): string {
  return formatDuration((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60_000)
}

/**
 * What the job is, in the words the office used: its kind of work. Never the
 * title — nobody names a job (rule 19).
 */
export function workOf(job: { category?: { name: string } | null }): string | null {
  return job.category?.name ?? null
}

export function suburbOf(
  address: { suburb?: string | null; city?: string | null } | null | undefined,
): string | null {
  return address?.suburb ?? address?.city ?? null
}

/** "Termite inspection · Inglewood", skipping whichever half is missing. */
export function workAndSuburb(
  job: { category?: { name: string } | null },
  address: { suburb?: string | null; city?: string | null } | null | undefined,
): string {
  return [workOf(job), suburbOf(address)].filter(Boolean).join(' · ')
}

/** "in 47m", "now", "25m ago". */
export function untilLabel(startAt: Date | string): string {
  const minutes = Math.round((new Date(startAt).getTime() - Date.now()) / 60_000)
  if (minutes === 0) return 'now'
  if (minutes > 0) return `in ${formatDuration(minutes)}`
  return `${formatDuration(-minutes)} ago`
}

type KnockSite = {
  hazardNotes?: string | null
  accessNotes?: string | null
  accessMethod?: string | null
  gateCode?: string | null
  hasPets?: boolean | null
  hasChildren?: boolean | null
}

export function siteChips(site: KnockSite): string[] {
  const chips: string[] = []
  if (site.hasPets) chips.push('Pets')
  if (site.hasChildren) chips.push('Children')
  if (site.accessMethod === 'CLIENT_PRESENT') chips.push('Client lets you in')
  if (site.gateCode) chips.push(`Code ${site.gateCode}`)
  return chips
}

export function hasKnockNotes(site: KnockSite | null | undefined): site is KnockSite {
  return Boolean(site && (site.hazardNotes || site.accessNotes || siteChips(site).length > 0))
}

/**
 * What to know before knocking: the hazard, the dog, the code for the gate.
 * Outside any row link, so reading a gate code never opens anything.
 */
export function KnockNotes({ site, showAccess = false }: { site: KnockSite; showAccess?: boolean }) {
  const chips = siteChips(site)
  return (
    <div className="flex flex-col gap-2">
      {site.hazardNotes ? (
        <p className="flex gap-2 rounded-[10px] bg-amber-50 px-3 py-2.5 text-sm leading-snug text-amber-900 ring-1 ring-amber-200 ring-inset">
          <Icon name="alert" className="mt-px size-[18px] shrink-0" />
          <span>{site.hazardNotes}</span>
        </p>
      ) : null}
      {showAccess && site.accessNotes ? (
        <p className="text-sm leading-snug text-ink-700">{site.accessNotes}</p>
      ) : null}
      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip}
              className="inline-flex h-7 items-center rounded-full bg-ink-100 px-2.5 text-[13px] font-medium text-ink-700 ring-1 ring-ink-200 ring-inset"
            >
              {chip}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The one button that moves a booking along: On my way, I've arrived,
 * Finished here. Only the next step, because it is pressed at a front door.
 *
 * The refusal — almost always "the report isn't done" — is drawn above it in
 * red, with Open report beside it when the caller knows which report.
 */
export function NextStepButton({
  visitId,
  status,
  onOpenReport,
  tone = 'light',
  className,
}: {
  visitId: string
  status: string
  onOpenReport?: () => void
  /** `dark` sits on the job's live card. */
  tone?: 'light' | 'dark'
  className?: string
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const checkIn = useMutation(
    trpc.schedule.checkIn.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      },
    }),
  )

  const next = nextCheckIn(status)
  const dark = tone === 'dark'

  return (
    <div className={cn('flex min-w-0 flex-1 flex-col gap-2', className)}>
      {checkIn.error ? (
        <div
          role="alert"
          className={cn(
            'flex items-center gap-2.5 rounded-xl px-3 py-2.5',
            dark ? 'bg-red-400/15' : 'bg-red-50 ring-1 ring-red-200 ring-inset',
          )}
        >
          <p className={cn('min-w-0 flex-1 text-[13.5px] leading-snug', dark ? 'text-red-100' : 'text-red-800')}>
            {errorMessage(checkIn.error)}
          </p>
          {onOpenReport ? (
            <button
              type="button"
              onClick={onOpenReport}
              className={cn(
                'shrink-0 px-3 py-1.5 text-[13px] font-semibold',
                dark ? 'rounded-full bg-white text-ink-900' : 'rounded-lg bg-red-700 text-white',
              )}
            >
              Open report
            </button>
          ) : null}
        </div>
      ) : null}

      {next ? (
        <button
          type="button"
          disabled={checkIn.isPending}
          onClick={() => checkIn.mutate({ id: visitId, status: next.status })}
          className="flex h-[54px] w-full items-center justify-center gap-2 rounded-[14px] bg-brand-600 text-[17px] font-semibold text-white transition-colors active:bg-brand-700 disabled:opacity-70"
        >
          {checkIn.isPending ? <Spinner className="size-5 text-white" /> : null}
          {next.label}
        </button>
      ) : status === 'COMPLETED' ? (
        <div
          className={cn(
            'flex h-[54px] w-full items-center justify-center gap-2 rounded-[14px] text-[17px] font-semibold',
            dark ? 'bg-white/10 text-emerald-200' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 ring-inset',
          )}
        >
          <Icon name="check" className="size-5" />
          Completed
        </div>
      ) : (
        <div
          className={cn(
            'flex h-[54px] w-full items-center justify-center rounded-[14px] text-[15px] font-semibold',
            dark ? 'bg-white/10 text-ink-300' : 'bg-ink-100 text-ink-600',
          )}
        >
          {humanise(status)}
        </div>
      )}
    </div>
  )
}

/** The square Call and Go tiles beside the next step. */
export function DockTile({
  icon,
  label,
  href,
  onClick,
  size = 54,
}: {
  icon: 'phone' | 'map' | 'mail' | 'activity'
  label: string
  href?: string | null
  onClick?: () => void
  size?: 54 | 56
}) {
  const className = cn(
    'flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-[14px] bg-ink-100 text-[11px] font-semibold text-ink-900 ring-1 ring-ink-200 ring-inset transition-colors active:bg-ink-200',
    size === 56 ? 'size-14' : 'size-[54px]',
  )

  if (href) {
    const external = href.startsWith('http')
    return (
      <a
        href={href}
        aria-label={label}
        className={className}
        {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
      >
        <Icon name={icon} className="size-5" />
        {label}
      </a>
    )
  }

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={!onClick}
      className={cn(className, 'disabled:opacity-40')}
    >
      <Icon name={icon} className="size-5" />
      {label}
    </button>
  )
}

/**
 * A layer over the open job: the feed, the bill, a note. A sheet from the
 * bottom on a phone and a dialog on an iPad, the way the job itself is.
 *
 * The body scrolls and `footer` stays pinned under it, over the home
 * indicator, because that is where the thumb is.
 */
export function FieldLayer({
  title,
  subtitle,
  onClose,
  footer,
  size = 'tall',
  children,
}: {
  title: string
  subtitle?: string
  onClose: () => void
  footer?: ReactNode
  /** `auto` takes only the room its contents need: a note, not a feed. */
  size?: 'tall' | 'auto'
  children: ReactNode
}) {
  const tablet = useIsTablet()
  const id = useId()

  const heading = (
    <div className="min-w-0 flex-1">
      <h2 id={id} className="truncate text-[20px] leading-tight font-bold text-ink-900">
        {title}
      </h2>
      {subtitle ? <p className="truncate text-[13px] text-ink-500">{subtitle}</p> : null}
    </div>
  )

  const body = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
      {footer ? (
        <div className="shrink-0 bg-white px-4 pt-2.5 pb-[calc(var(--safe-bottom)+10px)] shadow-[inset_0_1px_0_var(--color-ink-200)]">
          <div className="mx-auto flex w-full max-w-[680px] gap-2">{footer}</div>
        </div>
      ) : null}
    </>
  )

  if (tablet) {
    return (
      <Modal
        open
        onClose={onClose}
        labelledBy={id}
        className={cn(
          'max-w-[720px] rounded-[18px] bg-ink-100',
          size === 'tall' && 'h-[min(780px,calc(100vh-2rem))]',
        )}
      >
        <div className="flex shrink-0 items-center gap-3 bg-white px-4 py-3 shadow-[inset_0_-1px_0_var(--color-ink-200)]">
          {heading}
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 px-1 py-1.5 text-[17px] font-semibold text-brand-700"
          >
            Done
          </button>
        </div>
        {body}
      </Modal>
    )
  }

  return (
    <Sheet
      size={size}
      scrollBody={false}
      labelledBy={id}
      onClose={onClose}
      header={heading}
      className="bg-ink-100"
    >
      {body}
    </Sheet>
  )
}

/** The dock's big buttons: 54px, one primary and one quiet beside it. */
export const dockButtonClass =
  'flex h-[54px] min-w-0 items-center justify-center gap-2 rounded-[14px] px-3 text-base font-semibold transition-colors disabled:opacity-40'

export const dockPrimaryClass = cn(dockButtonClass, 'bg-brand-600 text-white active:bg-brand-700')

export const dockQuietClass = cn(
  dockButtonClass,
  'bg-white text-ink-800 ring-1 ring-ink-300 ring-inset active:bg-ink-50',
)

export function telHref(phone: string | null | undefined): string | null {
  return phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : null
}

/**
 * Directions to the site in whatever maps app the phone opens a Google link
 * with. Coordinates when the import has them, the address otherwise.
 */
export function directionsHref(
  address:
    | { latitude?: number | null; longitude?: number | null; raw?: string | null }
    | null
    | undefined,
  line: string | null,
): string | null {
  if (address?.latitude != null && address.longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${address.latitude},${address.longitude}`
  }
  const text = line ?? address?.raw
  return text && text !== '—'
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(text)}`
    : null
}
